/*
 * Recently viewed — browser-based ("Pick up where you left off") product
 * history for the AlCell theme.
 *
 * Two responsibilities, both driven by markup so no per-page glue is needed:
 *
 *   1. TRACK  — on a product page, snippets/recently-viewed-track.liquid emits a
 *      <script type="application/json" data-rv-current> snapshot of the current
 *      product. We unshift it (most-recent-first, deduped) into localStorage.
 *
 *   2. RENDER — on the homepage, sections/recently-viewed.liquid ships a hidden,
 *      empty carousel shell ([data-rv-root] with a [data-rv-track]). We rebuild
 *      cards from localStorage into the track and reveal the section. The card
 *      markup mirrors snippets/featured-tabs-card.liquid exactly, so the shared
 *      section-featured-tabs.css/.js (arrows, mobile swipe, hover controls) and
 *      the global wishlist.js delegation (heart + fast Add-to-Cart) all apply.
 *
 * Storage shape (localStorage key "alcell:recently-viewed"), newest first:
 *   [{ id, handle, title, url, image, price, compare, available, variant, new }]
 *   price/compare are integer cents (Shopify money), formatted on render.
 *
 * It works for guests and logged-in customers, needs no app/backend, and stays
 * on-device (no cross-device sync — the same metafield/app upgrade path noted in
 * wishlist.js applies here too).
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'alcell:recently-viewed';
  var STORE_LIMIT = 20; // keep a few more than we show, so the cap never starves the view

  /* ---------- storage ---------- */

  function read() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      /* storage full / disabled — fail silently */
    }
  }

  // Move/insert `item` to the front, deduped by id, capped at STORE_LIMIT.
  function record(item) {
    if (!item || item.id == null) return;
    var list = read().filter(function (i) {
      return String(i.id) !== String(item.id);
    });
    list.unshift(item);
    write(list.slice(0, STORE_LIMIT));
  }

  /* ---------- helpers ---------- */

  // Format integer cents as "R 1,234" — mirrors the price-rand Liquid snippet
  // (whole rands, comma thousands separators, no decimals).
  function formatRand(cents) {
    var rands = Math.floor((parseInt(cents, 10) || 0) / 100);
    return 'R ' + String(rands).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ---------- track (product pages) ---------- */

  function trackCurrent() {
    var el = document.querySelector('[data-rv-current]');
    if (!el) return;
    var data;
    try {
      data = JSON.parse(el.textContent);
    } catch (e) {
      return;
    }
    record(data);
  }

  /* ---------- live prices ----------
     Every other price on the site is rendered by Shopify in the buyer's own
     catalog context. This carousel is the one exception: it replays a snapshot
     taken whenever the product happened to be viewed, which makes the stored
     price wrong in both directions — a card recorded before a dealer signed
     their agreement holds a retail price, and one recorded by a signed dealer
     holds a trade price that must never be shown to a retail visitor.

     A card whose price is in doubt is re-read from Shopify's product JSON,
     which is itself contextualised for whoever is asking.

     What is NOT done here is wait for those reads before drawing anything.
     Blocking the whole section on Promise.all meant a signed-in dealer sat on
     the pulsing skeleton until the slowest of up to twelve product fetches
     came back, while a retail visitor — who fetches nothing — got the section
     instantly. That difference is the entire reason the section "loaded
     poorly" for dealers, and it was paying that cost on every card to protect
     against a case that applies to almost none of them. */

  function viewingAsB2B() {
    return document.documentElement.classList.contains('is-dealer-b2b');
  }

  // What may be painted for this card right now:
  //
  //   'ok'      the stored price belongs to this buyer's context — paint it.
  //   'verify'  recorded in this same context, so it is right unless the
  //             dealer's catalog moved under them (signing the agreement, or
  //             an IQ price sync). Paint it now, confirm in the background.
  //   'confirm' recorded in the OTHER context, so the figure on file is for
  //             the wrong buyer. Withhold the card behind its skeleton until
  //             re-read — this is the case that must never leak a trade price
  //             to a retail visitor, so it is the only one that blocks.
  function priceState(item, asB2B) {
    // Fallback products come from Liquid, already in the current context.
    if (!item || item.fresh || !item.handle) return 'ok';
    if (Boolean(item.b2b) !== asB2B) return 'confirm';
    return asB2B ? 'verify' : 'ok';
  }

  function fetchLivePrice(item) {
    return fetch('/products/' + encodeURIComponent(item.handle) + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        var variants = (data && data.variants) || [];
        var match = variants.filter(function (v) {
          return String(v.id) === String(item.variant);
        })[0] || variants[0];
        if (!match) return null;
        return {
          price: match.price,
          compare: match.compare_at_price || 0,
          available: Boolean(match.available)
        };
      })
      .catch(function () { return null; });
  }

  // Re-reads run per card and patch in place, so a slow response holds up only
  // its own card instead of the whole row.
  function resolveLivePrices(root, track, list, asB2B) {
    list.forEach(function (item, index) {
      var state = priceState(item, asB2B);
      if (state === 'ok') return;

      fetchLivePrice(item).then(function (live) {
        var node = track.querySelector('[data-rv-index="' + index + '"]');
        if (!node) return;

        if (!live) {
          // Unconfirmable. A dealer keeping a slightly stale figure is untidy;
          // a retail visitor seeing a trade price is not acceptable — so only
          // the withheld card is dropped.
          if (state === 'confirm') {
            node.remove();
            if (!track.children.length) root.setAttribute('hidden', '');
          }
          return;
        }

        var moved =
          String(item.price) !== String(live.price) ||
          String(item.compare || 0) !== String(live.compare || 0) ||
          Boolean(item.available) !== live.available;

        item.price = live.price;
        item.compare = live.compare;
        item.available = live.available;

        // A 'verify' card is already correct almost every time — only redraw it
        // when the catalog actually moved, so nothing flickers for no reason.
        if (state !== 'confirm' && !moved) return;

        node.outerHTML = cardHTML(item, index);
        if (window.Wishlist && typeof window.Wishlist.refresh === 'function') {
          window.Wishlist.refresh();
        }
      });
    });
  }

  /* ---------- render (homepage carousel) ---------- */

  // Stands in for a card whose price is still being confirmed. Identical to the
  // markup the section's pre-paint bootstrap uses, so a withheld card is simply
  // a skeleton that has not resolved yet — indistinguishable from one still
  // hydrating, and carrying no price to leak.
  function skeletonHTML(index) {
    return '' +
      '<article class="ftabs__card ftabs__card--skeleton" aria-hidden="true" data-rv-index="' + index + '">' +
        '<div class="ftabs__media"></div>' +
        '<div class="ftabs__body">' +
          '<div class="ftabs__skeleton-line"></div>' +
          '<div class="ftabs__skeleton-line ftabs__skeleton-line--short"></div>' +
        '</div>' +
      '</article>';
  }

  // Rebuild a single card. Mirrors snippets/featured-tabs-card.liquid so it
  // inherits all of section-featured-tabs.css and the wishlist.js delegation.
  // `index` is the card's slot in the row, so a late price can find and replace
  // it without disturbing the order.
  function cardHTML(item, index) {
    var price = parseInt(item.price, 10) || 0;
    var compare = parseInt(item.compare, 10) || 0;
    var onSale = compare > price;

    var badge = '';
    if (item['new']) {
      badge = '<span class="ftabs__badge ftabs__badge--new">NEW</span>';
    } else if (onSale) {
      // Integer division to match the Liquid badge (… | times: 100 | divided_by: compare).
      var pct = Math.floor((compare - price) * 100 / compare);
      badge = '<span class="ftabs__badge ftabs__badge--sale">-' + pct + '%</span>';
    }

    var media = item.image
      ? '<img src="' + esc(item.image) + '" alt="' + esc(item.title) + '" class="ftabs__img" loading="lazy">'
      : '<div class="ftabs__img ftabs__img--placeholder"></div>';

    // data-dealer-* lets dealer-pricing.js swap in the trade price, exactly as
    // it does for the Liquid-rendered cards elsewhere. The recorded list price
    // (sirair.list_price, captured on the product page) rides along so B2B
    // sessions resolve DEALER/RETAIL — or N/A — with no app lookup; entries
    // saved before this field existed fall back to the app-streamed path.
    var dealerAttrs =
      ' data-dealer-price="' + price + '" data-dealer-variant="' + esc(item.variant) + '"' +
      ' data-dealer-handle="' + esc(item.handle) + '"' +
      ' data-dealer-retail="' + (parseInt(item.retail, 10) || 0) + '"';
    var priceRow = onSale
      ? '<span class="ftabs__price ftabs__price--sale"' + dealerAttrs + '>' + esc(formatRand(price)) + '</span>' +
        '<span class="ftabs__price ftabs__price--compare">' + esc(formatRand(compare)) + '</span>'
      : '<span class="ftabs__price"' + dealerAttrs + '>' + esc(formatRand(price)) + '</span>';

    var multi = ''; // recently-viewed only stores the first variant; treat as single-add
    var atc = item.available
      ? '<a href="' + esc(item.url) + '" class="ftabs__atc" tabindex="-1"' +
          ' aria-label="Add ' + esc(item.title) + ' to cart" data-card-atc' +
          ' data-url="' + esc(item.url) + '" data-variant="' + esc(item.variant) + '"' + multi + '>' +
          'Add To Cart' +
          '<svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>' +
        '</a>'
      : '<span class="ftabs__atc ftabs__atc--oos" aria-hidden="true">Out of Stock</span>';

    return '' +
      '<article class="ftabs__card" tabindex="0" data-rv-index="' + index + '">' +
        '<div class="ftabs__media">' +
          '<a href="' + esc(item.url) + '" class="ftabs__media-link" aria-label="' + esc(item.title) + '">' +
            badge + media +
          '</a>' +
          '<button type="button" class="ftabs__wishlist" data-wishlist-toggle' +
            ' data-wishlist-id="' + esc(item.id) + '"' +
            ' data-wishlist-handle="' + esc(item.handle) + '"' +
            ' data-wishlist-title="' + esc(item.title) + '"' +
            ' data-wishlist-url="' + esc(item.url) + '"' +
            ' data-wishlist-image="' + esc(item.image) + '"' +
            ' data-wishlist-price="' + esc(price) + '"' +
            ' data-wishlist-compare="' + esc(compare) + '"' +
            ' data-wishlist-available="' + (item.available ? 'true' : 'false') + '"' +
            ' data-wishlist-variant="' + esc(item.variant) + '"' +
            ' aria-pressed="false" aria-label="Add to wishlist">' +
            '<svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>' +
          '</button>' +
          atc +
        '</div>' +
        '<div class="ftabs__body">' +
          '<a href="' + esc(item.url) + '" class="ftabs__title-link">' +
            '<p class="ftabs__title">' + esc(item.title) + '</p>' +
          '</a>' +
          '<div class="ftabs__price-row">' + priceRow + '</div>' +
        '</div>' +
      '</article>';
  }

  // Fallback / recommended products emitted server-side (data-rv-fallback),
  // used to top up a thin row so it never looks half-empty.
  function readFallback(root) {
    var el = root.querySelector('[data-rv-fallback]');
    if (!el) return [];
    try {
      var list = JSON.parse(el.textContent);
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  // Append items from `pool` not already present (by id) until `list` reaches
  // `target` (or the pool runs out).
  function backfill(list, pool, target) {
    var seen = {};
    list.forEach(function (i) { seen[String(i.id)] = true; });
    for (var k = 0; k < pool.length && list.length < target; k++) {
      var item = pool[k];
      if (item && !seen[String(item.id)]) {
        list.push(item);
        seen[String(item.id)] = true;
      }
    }
  }

  function renderRoots() {
    document.querySelectorAll('[data-rv-root]').forEach(function (root) {
      var track = root.querySelector('[data-rv-track]');
      if (!track) return;

      var min = parseInt(root.getAttribute('data-rv-min'), 10);
      if (isNaN(min)) min = 6;
      var max = parseInt(root.getAttribute('data-rv-max'), 10) || 12;

      var recent = read();
      var fallback = readFallback(root);
      var designMode = window.Shopify && window.Shopify.designMode;

      var list;
      if (recent.length) {
        // Real history leads; top up with recommendations only if it's thin.
        list = recent.slice();
        if (list.length < min) backfill(list, fallback, min);
        list = list.slice(0, max);
      } else if (designMode && fallback.length) {
        // No history in the editor — preview the layout with the fallback set.
        list = fallback.slice(0, max);
      } else if (designMode) {
        // No history and no fallback configured: explain rather than show blank.
        track.innerHTML = '<p class="ftabs__empty">Each visitor sees the products they recently viewed here ' +
          '(topped up from the fallback collection). It is empty in the editor because there is no browsing history.</p>';
        root.removeAttribute('hidden');
        return;
      } else {
        // First-time visitor on the live site: stay hidden.
        track.innerHTML = '';
        root.setAttribute('hidden', '');
        return;
      }

      // Draw straight away. Every card whose stored price belongs to this
      // buyer's context goes up now — for a signed-in dealer browsing their own
      // history that is all of them, so the row appears exactly as fast as it
      // does for a retail visitor. Only cards recorded in the other context are
      // held back as skeletons, and resolveLivePrices fills or drops each one
      // as its own re-read lands.
      var asB2B = viewingAsB2B();
      track.innerHTML = list
        .map(function (item, index) {
          return priceState(item, asB2B) === 'confirm'
            ? skeletonHTML(index)
            : cardHTML(item, index);
        })
        .join('');
      root.removeAttribute('hidden');

      // Let the shared wishlist.js sync heart states on the freshly-built cards.
      if (window.Wishlist && typeof window.Wishlist.refresh === 'function') {
        window.Wishlist.refresh();
      }

      resolveLivePrices(root, track, list, asB2B);
    });
  }

  /* ---------- init ---------- */

  function init() {
    trackCurrent(); // no-op when not on a product page
    renderRoots();  // no-op when no carousel on the page
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-render when the section reloads in the theme editor.
  document.addEventListener('shopify:section:load', renderRoots);

  // Keep the carousel in sync if another tab records a new view.
  window.addEventListener('storage', function (e) {
    if (e.key === STORAGE_KEY) renderRoots();
  });
})();
